import React, { useEffect, useState } from 'react';

import { Form, Modal } from 'antd';
import styled from 'styled-components';

import { getApartmentTypeGroupSlug } from './helpers';
import { useContractContext } from '../../../common/Contract/ContractContext';
import DownCaretIcon from '../../../common/Icons/DownCaretIcon';
import { useAppSelector } from '../../../store/hook';
import { ApartmentTypeGroup } from '../../../types/accommodations';
import Button from '../../lib/Button';
import Select from '../../lib/Select';
import { FIELD_CATEGORY } from '../constants';
import { useShopDataModalContext } from '../ShopDataModalContext';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 30px 30px 20px 30px;
`;

const StyledModal = styled(Modal)`
  &&& {
    .ant-modal-content {
      padding: 0px;
      line-height: 1;
      border-radius: 1px;
      user-select: none;
    }
    .ant-modal-body {
      padding: 0px;
      line-height: normal;
    }
  }
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
`;

const Title = styled.span`
  font-size: 20px;
  font-family: var(--font-bold);
  color: var(--text-primary);
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 30px;
`;

const Info = styled.span`
  display: flex;
  flex-direction: column;
  font-size: 14px;
  color: var(--text-secondary);
`;

const Fields = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const Field = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const Label = styled.div`
  font-family: var(--font-bold);
  font-size: 14px;
  text-align: right;
  min-width: 100px;
  color: var(--text-primary);
`;

const Footer = styled.div`
  display: flex;
  border-top: 1px solid var(--line-gray);
  padding: 20px 30px;
  gap: 10px;
  justify-content: end;
`;

interface Props {
  open: boolean;
  onClose: () => void;
}

const ShopDataModalAccommodationsAddRoomTypeModal = ({ open, onClose }: Props) => {
  const apartmentTypeGroups = useAppSelector((state) => state.global.apartmentTypeGroups);
  const { currentData, currentLivingType, setCurrentData } = useShopDataModalContext();
  const { selectedKey } = useContractContext();
  const [roomTypeGroupsOptions, setRoomTypeGroupsOptions] = useState<any>();

  const onFinish = (values: any) => {
    const groupKey = getApartmentTypeGroupSlug(values.roomTypeGroup);
    const currentLivingTypeKey = currentLivingType?.abv;

    setCurrentData((prevState: any) => ({
      ...prevState,
      [FIELD_CATEGORY.ACCOMMODATIONS]: {
        ...prevState[FIELD_CATEGORY.ACCOMMODATIONS],
        [currentLivingTypeKey]: {
          ...prevState[FIELD_CATEGORY.ACCOMMODATIONS][currentLivingTypeKey],
          [selectedKey as string]: {
            ...prevState[FIELD_CATEGORY.ACCOMMODATIONS][currentLivingTypeKey]?.[selectedKey as string],
            [groupKey]: {},
          },
        },
      },
    }));
    onClose();
  };

  useEffect(() => {
    const currentLivingTypeKey = currentLivingType?.abv;
    const currentAccommodationData = currentData[FIELD_CATEGORY.ACCOMMODATIONS] ?? {};
    const currentLivingTypeData = currentAccommodationData[currentLivingTypeKey] ?? {};
    const currentLivingTypeContractData = selectedKey ? currentLivingTypeData[selectedKey] ?? {} : {};
    const existingLivingTypeApartmentTypeGroups = Object.keys(currentLivingTypeContractData ?? {});

    const options = apartmentTypeGroups
      .filter(
        (item: ApartmentTypeGroup) =>
          !existingLivingTypeApartmentTypeGroups.includes(getApartmentTypeGroupSlug(item.id))
      )
      .map((item: ApartmentTypeGroup) => ({
        value: item.id,
        label: item.name,
      }))
      .sort((a: any, b: any) => a.value - b.value);

    setRoomTypeGroupsOptions(options);
  }, [currentData]);

  const initialValues = {
    roomTypeGroup: undefined,
  };

  return (
    <StyledModal open={open} footer={null} destroyOnClose onCancel={onClose}>
      <Form initialValues={initialValues} onFinish={onFinish}>
        <Container>
          <Header>
            <Title data-test-id='add-room-type-modal-title'>Add Room Type</Title>
          </Header>
          <Content>
            <Info data-test-id='add-room-type-modal-text'>
              <span>Choose room type below.</span>
              <span>You will be able to add its variants, such as Plus and Deluxe, later.</span>
            </Info>
            <Fields>
              <Field>
                <Label>Room Type</Label>
                <Form.Item
                  name={'roomTypeGroup'}
                  noStyle
                  rules={[{ required: true, message: 'Please select room type group.' }]}
                >
                  <Select
                    className="rectangular"
                    style={{ width: '100%', height: 36 }}
                    options={roomTypeGroupsOptions}
                    suffixIcon={<DownCaretIcon />}
                    placeholder={'Select room type'}
                    disabled={roomTypeGroupsOptions?.length === 0}
                    dropdownStyle={{
                      padding: 0,
                      borderRadius: 0,
                      border: '1px solid var(--line-gray)',
                      boxShadow: '0 2px 4px 0 rgba(0, 0, 0, 0.1)',
                    }}
                    data-test-id='add-room-type-modal-dropdown'
                  />
                </Form.Item>
              </Field>
            </Fields>
          </Content>
        </Container>
        <Footer>
          <Button
            type={'primary'}
            style={{ fontFamily: 'var(--font-bold)', height: 40, width: 120 }}
            disabled={roomTypeGroupsOptions?.length === 0}
            htmlType="submit"
            data-test-id='add-room-type-modal-add-btn'
          >
            Add
          </Button>
          <Button style={{ fontFamily: 'var(--font-bold)', height: 40, width: 120 }} onClick={onClose} data-test-id='add-room-type-modal-cancel-btn'>
            Cancel
          </Button>
        </Footer>
      </Form>
    </StyledModal>
  );
};

export default ShopDataModalAccommodationsAddRoomTypeModal;
